import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { enum2NumberArray } from 'core/utils/commonUtils';
import { getMeasureName } from 'core/utils/productUtils';
import { isEmpty, isNil } from 'lodash';
import { MeasureEnum } from 'models/enums/measureEnum';
import { useEffect, useMemo } from 'react';
import {
  FormContainer,
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
} from 'react-hook-form-mui';
import { array, date, number, object, string } from 'yup';
import MarkupsEdit from './MarkupsEdit';
import type { ProjectProduct, ProjectProductMarkup } from 'models/order/orderProject';
import type { FC } from 'react';
import type { Schema } from 'yup';

type FormData = Pick<
  ProjectProduct,
  'name' | 'measure' | 'count' | 'price' | 'remark'
> & {
  markups: ProjectProductMarkup[];
};

const schema: Schema<FormData> = object().shape({
  name: string().required(),
  measure: number().oneOf(enum2NumberArray(MeasureEnum)).required(),
  count: number().required().min(0).max(1_000_000_000),
  price: number().required().min(-1_000_000_000).max(1_000_000_000),
  remark: string().max(1000).ensure(),
  markups: array()
    .of(
      object().shape({
        id: number().required().default(0),
        userId: number().required().default(0),
        remark: string().ensure(),
        amountPerItem: number().min(0).default(0).max(1_000_000_000),
        user: object()
          .shape({
            id: number().required(),
            fullName: string().ensure(),
          })
          .required(),
        dateCreate: date().required(),
      })
    )
    .ensure()
    .required()
    .max(15),
});

const ProductEditDialog: FC<ProductEditDialogProps> = ({
  product,
  open,
  onCancel,
  onSave,
}) => {
  const formContext = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (open) formContext.reset(product);
  }, [formContext, open, product]);

  const handleOnSave: VoidFunction = () => {
    const formValue = formContext.getValues();

    const editedProduct = {
      ...product,
      ...formValue,
      markups: formValue.markups.map((m) => ({
        ...m,
        dateCreate: new Date(),
      })),
    };

    !isNil(onSave) && onSave(editedProduct);
  };

  const isNew = useMemo(() => isEmpty(product.name), [product]);

  const getTitle = (): string => (isNew ? 'Новый товар' : product.name);

  return (
    <SimpleDialog
      title={getTitle()}
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth={'sm'}
      actions={
        <>
          <Button
            onClick={() => {
              void formContext.handleSubmit(handleOnSave)();
            }}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
          <Button onClick={onCancel}>Закрыть</Button>
        </>
      }
      content={
        <FormContainer
          formContext={formContext}
          onSuccess={handleOnSave}
          FormProps={{ style: { display: 'flex', flexDirection: 'column' } }}>
          <TextFieldElement
            fullWidth
            label='Название'
            name='name'
            type='text'
            margin='normal'
            size='small'
            required
          />

          <SelectElement
            label='Единица измерения'
            name='measure'
            margin='normal'
            size='small'
            fullWidth
            required
            options={enum2NumberArray(MeasureEnum).map((v) => ({
              id: v,
              label: getMeasureName(v),
            }))}
          />

          <TextFieldElement
            fullWidth
            label='Количество'
            name='count'
            type='number'
            margin='normal'
            size='small'
            required
          />

          <TextFieldElement
            fullWidth
            label='Цена'
            name='price'
            type='number'
            margin='normal'
            size='small'
            required
          />

          <TextareaAutosizeElement
            fullWidth
            label='Примечание'
            name='remark'
            margin='normal'
            size='small'
          />

          <MarkupsEdit />
        </FormContainer>
      }
    />
  );
};

export interface ProductEditDialogProps {
  product: ProjectProduct;
  open: boolean;
  onSave?: (product: ProjectProduct) => void;
  onCancel?: VoidFunction;
}

export default ProductEditDialog;
